import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ColorsV2, getToken } from '@electricjs/arc';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/store';
import { closeDrawer } from '@/redux/slices/aiChatDrawerSlice';
import { AIChatCrashFallback } from '@/pages/AIChatCopilotKit/CopilotKitInitErrorBanner';
import { CopilotKitAIChatDrawerContent } from './CopilotKitAIChatDrawerContent';

/**
 * Outer wrapper: clips the inner panel as it slides in/out via width animation.
 *
 * Default width is 40rem. At viewports ≤ 160rem the panel narrows to 32rem
 * so it takes less space on typical desktop sizes.
 */
const PanelAnimationWrapper = styled.div<{ $isOpen: boolean }>`
  flex-shrink: 0;
  width: ${p => (p.$isOpen ? '40rem' : '0')};
  overflow: hidden;

  /* 100rem here = 1600px. Media queries always use the browser's default font
     size (16px) for rem, regardless of any custom font-size on html. Since this
     project sets html { font-size: 10px }, element rem values differ from media
     query rem values. Formula: desired px ÷ 16 = media query rem value. */
  @media (max-width: 100rem) {
    width: ${p => (p.$isOpen ? '32rem' : '0')};
  }

  @media (prefers-reduced-motion: no-preference) {
    transition: width 250ms ease-in-out;
  }
`;

/**
 * Inner panel: fills the wrapper. Visual separation from page content
 * via left border, background color, and a subtle left shadow.
 */
const PanelInner = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 2rem;
  box-sizing: border-box;
  overflow: hidden;
  border-left: 1px solid ${getToken(ColorsV2.BORDER)};
  background-color: ${getToken(ColorsV2.BACKGROUND_CONTENT)};
  visibility: ${p => (p.$isOpen ? 'visible' : 'hidden')};
  pointer-events: ${p => (p.$isOpen ? 'auto' : 'none')};
`;

/**
 * EmbeddedAIChatPanel
 *
 * Desktop-only embedded side panel for AI chat. Sits to the right of the
 * main content area as a flex sibling, pushing content left when open.
 *
 * Always mounted (never conditionally rendered) so chat state, scroll
 * position, and partially-typed input are preserved across open/close.
 *
 * On mobile, the existing CopilotKitAIChatDrawer overlay is used instead.
 */
export const EmbeddedAIChatPanel = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(state => state.aiChatDrawer.isOpen);
  const handleClose = useCallback(() => dispatch(closeDrawer()), [dispatch]);

  return (
    <PanelAnimationWrapper $isOpen={isOpen}>
      <PanelInner $isOpen={isOpen} aria-hidden={!isOpen}>
        <ErrorBoundary FallbackComponent={AIChatCrashFallback}>
          <CopilotKitAIChatDrawerContent
            onClose={handleClose}
            visible={isOpen}
          />
        </ErrorBoundary>
      </PanelInner>
    </PanelAnimationWrapper>
  );
};
